import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Logo from '../ui/Logo';
import Btn from '../ui/Btn';
import NotificationsBell from './NotificationsBell';
import { Avatar, Divider } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/theme';
import { useBreakpoint } from '../../lib/useBreakpoint';

/**
 * Shared top bar used by Hub and (later) workspace pages.
 *
 * Props:
 *   - breadcrumb:        optional ReactNode to show after the logo (used inside workspaces)
 *   - showNotifications: boolean — show the bell icon (default true)
 *   - notificationCount: optional number — show a dot if > 0
 *   - children:          extra content slotted before the help/bell/avatar cluster
 *   - onMenuClick:       optional fn — when provided, render a hamburger button that
 *                        opens the workspace drawer (mobile/tablet only). Parent
 *                        decides when to pass this (e.g. WorkspaceShell does).
 */
export default function AppTopBar({
  breadcrumb,
  showNotifications = true,
  notificationCount = 0,
  children,
  onMenuClick,
}) {
  const { user, signOut } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useBreakpoint();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const meta = user?.user_metadata || {};
  const fullName =
    [meta.first_name, meta.last_name].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    'You';
  const shortName = meta.first_name
    ? `${meta.first_name} ${(meta.last_name || '').charAt(0)}${meta.last_name ? '.' : ''}`.trim()
    : fullName;

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  // Hamburger only rendered when (a) parent passed onMenuClick and (b) we're
  // below desktop. Above 1024px, sidebar is inline so the drawer is unused.
  const showHamburger = onMenuClick && !isDesktop;

  return (
    <div style={{
      height: 56, background: 'var(--surface)',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center',
      padding: isMobile ? '0 12px' : '0 20px', gap: isMobile ? 8 : 12,
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      {showHamburger && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation"
          style={{
            width: 40, height: 40, marginRight: 2,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', borderRadius: 8,
            color: 'var(--text-2)', cursor: 'pointer',
          }}
        >
          <Icon.menu size={20} />
        </button>
      )}
      <Link to="/hub" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
        <Logo size={36} useFile />
      </Link>

      {breadcrumb && (
        <>
          <Icon.chevR size={14} stroke="var(--text-muted)" style={{ flexShrink: 0 }} />
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 13, fontWeight: 600,
            flex: 1, minWidth: 0, overflow: 'hidden',
          }}>
            {breadcrumb}
          </div>
        </>
      )}

      {!breadcrumb && <div style={{ flex: 1 }} />}

      {children}

      {/* Theme toggle */}
      <Btn
        variant="ghost"
        size="sm"
        icon={theme === 'dark' ? Icon.sun : Icon.moon}
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      />

      {/* Notifications bell */}
      {showNotifications && <NotificationsBell />}

      <div style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 4px' }} />

      {/* Avatar menu */}
      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 10px 4px 4px', borderRadius: 20,
            cursor: 'pointer', border: 'none',
            background: menuOpen ? 'var(--surface-2)' : 'transparent',
            fontFamily: 'inherit', color: 'var(--text)',
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { if (!menuOpen) e.currentTarget.style.background = 'var(--surface-2)'; }}
          onMouseLeave={(e) => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}
        >
          <Avatar name={fullName} size={28} color="#3dc75b" />
          {!isMobile && (
            <>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{shortName}</span>
              <Icon.chevD size={12} stroke="var(--text-muted)" />
            </>
          )}
        </button>

        {menuOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 220,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-lg)',
            padding: 6, zIndex: 100,
          }}>
            <div style={{ padding: '10px 10px 6px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fullName}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{user?.email}</div>
            </div>
            <Divider style={{ margin: '6px 0' }} />
            <MenuItem icon={Icon.user} label="Account settings" onClick={() => { setMenuOpen(false); navigate('/account'); }} />
            <MenuItem icon={Icon.home} label="Workspace hub" onClick={() => { setMenuOpen(false); navigate('/hub'); }} />
            <Divider style={{ margin: '6px 0' }} />
            <MenuItem icon={Icon.logout} label="Sign out" onClick={handleSignOut} danger />
          </div>
        )}
      </div>
    </div>
  );
}

function MenuItem({ icon: I, label, onClick, danger }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '8px 10px', textAlign: 'left',
        border: 'none', cursor: 'pointer',
        background: hover ? 'var(--surface-2)' : 'transparent',
        color: danger ? 'var(--danger)' : 'var(--text)',
        fontSize: 13, fontFamily: 'inherit',
        borderRadius: 6, transition: 'background 0.1s',
      }}
    >
      <I size={14} />
      {label}
    </button>
  );
}
